import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, 
  Users, 
  MapPin,
  Trash2,
  Search,
  Home,
  Mail,
  User,
  Pencil,
  Phone
} from 'lucide-react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';

export default function SuperAdminBuildingsPage() {
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, building: null });
  const [deleting, setDeleting] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, building: null });
  const [waNumber, setWaNumber] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBuildings();
  }, []);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/buildings`);
      setBuildings(response.data);
    } catch (error) {
      console.error('Error fetching buildings:', error);
      toast.error('Error al cargar conjuntos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/super-admin/buildings/${deleteDialog.building?.id}`);
      toast.success('Conjunto eliminado correctamente');
      setDeleteDialog({ open: false, building: null });
      fetchBuildings();
    } catch (error) {
      console.error('Error deleting building:', error);
      toast.error('Error al eliminar conjunto');
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (building) => {
    setWaNumber(building.whatsapp_number || '');
    setEditDialog({ open: true, building });
  };

  const handleSaveWhatsApp = async () => {
    const cleaned = waNumber.replace(/[\s\-+]/g, '');
    if (cleaned && !/^\d{8,15}$/.test(cleaned)) {
      toast.error('Número inválido: usa código de país + número, solo dígitos (8-15)');
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/buildings/${editDialog.building?.id}`, { whatsapp_number: cleaned });
      toast.success('Número de WhatsApp actualizado');
      setEditDialog({ open: false, building: null });
      fetchBuildings();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al actualizar el número');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredBuildings = buildings.filter(building =>
    building.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    building.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    building.admin_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="super-admin-buildings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Todos los Conjuntos</h1>
            <p className="text-slate-500 mt-1">
              {buildings.length} conjunto(s) registrado(s) en la plataforma
            </p>
          </div>
        </div>

        {/* Search */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, ciudad o administrador..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="building-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Buildings Grid */}
        {filteredBuildings.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building2 className="h-12 w-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No se encontraron conjuntos</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBuildings.map((building) => (
              <Card 
                key={building.id} 
                className="border-slate-200 hover:shadow-lg transition-shadow"
                data-testid={`building-card-${building.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-xl bg-orange-100 flex items-center justify-center">
                        {building.logo_url ? (
                          <img 
                            src={building.logo_url} 
                            alt={building.name} 
                            className="h-12 w-12 rounded-xl object-cover"
                          />
                        ) : (
                          <Building2 className="h-6 w-6 text-orange-500" />
                        )}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{building.name}</CardTitle>
                        <CardDescription className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {building.city || 'Sin ciudad'}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(building)}
                        className="text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                        data-testid={`edit-building-${building.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteDialog({ open: true, building })}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-building-${building.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      <span className="truncate">{building.address}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Home className="h-4 w-4 text-slate-400" />
                      <span>{building.units || 0} unidades</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <span data-testid={`building-whatsapp-${building.id}`} className={building.whatsapp_number ? '' : 'text-slate-400 italic'}>
                        {building.whatsapp_number ? `+${building.whatsapp_number}` : 'WhatsApp sin configurar'}
                      </span>
                    </div>

                    <div className="pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-400 mb-1">Administrador</p>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-700">
                          {building.admin_name || 'Sin asignar'}
                        </span>
                      </div>
                      {building.admin_email && (
                        <div className="flex items-center gap-2 mt-1">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <span className="text-sm text-slate-500 truncate">
                            {building.admin_email}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-slate-100 text-xs text-slate-400">
                      <span>Creado: {formatDate(building.created_at)}</span>
                      <span className="font-mono bg-slate-100 px-2 py-1 rounded">
                        {building.invitation_code}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit WhatsApp Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, building: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Número de WhatsApp</DialogTitle>
              <DialogDescription>
                Configura el número de WhatsApp (canal Jelou) para "{editDialog.building?.name}".
                Usa código de país + número, sin espacios ni símbolos. Ej: 573001234567
              </DialogDescription>
            </DialogHeader>
            <Input
              value={waNumber}
              onChange={(e) => setWaNumber(e.target.value)}
              placeholder="573001234567"
              data-testid="whatsapp-number-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, building: null })}>
                Cancelar
              </Button>
              <Button
                onClick={handleSaveWhatsApp}
                disabled={saving}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="save-whatsapp-number"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, building: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Eliminar Conjunto</DialogTitle>
              <DialogDescription>
                ¿Estás seguro de eliminar "{deleteDialog.building?.name}"?
                <br /><br />
                <strong className="text-red-600">Esta acción eliminará permanentemente:</strong>
                <ul className="list-disc list-inside mt-2 text-slate-600">
                  <li>Todas las unidades</li>
                  <li>Todos los residentes</li>
                  <li>Todos los PQRS, anuncios, mantenimientos</li>
                  <li>Todo el historial financiero</li>
                </ul>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, building: null })}>
                Cancelar
              </Button>
              <Button 
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-500 hover:bg-red-600"
                data-testid="confirm-delete-building"
              >
                {deleting ? 'Eliminando...' : 'Eliminar Permanentemente'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
